import { useState, useMemo } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@/components/icons/NavIcons';

interface ProductCombination {
  id: string;
  levels: Record<string, string>;
  utility: number;
  share: number;
  rank: number;
}

export interface SimulationResultData {
  id: string;
  name: string;
  simulation_type: string;
  created_at: string;
  total_combinations: number;
  attributes_used: number;
  levels_used: number;
  top_products: ProductCombination[];
  all_products: ProductCombination[];
  attribute_importance: Record<string, number>;
  level_utilities: Record<string, Record<string, number>>;
  insights: string[];
}

// Market data for existing products
interface ExistingProduct {
  id: string;
  name: string;
  brand: string;
  manufacturer: string;
  share: number;
  isOwn: boolean;
  attributes: {
    flavor?: string;
    format?: string;
    positioning?: string;
    price?: number;
  };
}

interface SourceOfVolume {
  competitorConquest: number;
  ownCannibalization: number;
  marketExpansion: number;
}

interface ShareMovement {
  product: ExistingProduct;
  shareLost: number;
  newShare: number;
}

interface MarketImpactResult {
  newProductShare: number;
  sourceOfVolume: SourceOfVolume;
  shareMovements: ShareMovement[];
  cannibalizationRate: number;
  cannibalizationRisk: 'low' | 'medium' | 'high';
  insights: string[];
}

interface SimulationResultsProps {
  result: SimulationResultData;
  attributeNames: Record<string, string>;
  levelNames: Record<string, Record<string, string>>;
  clientId?: string;
  onClose?: () => void;
}

// Existing market data for Meals de Colombia
const MEALS_PORTFOLIO: ExistingProduct[] = [
  { id: 'POLET-MORA', name: 'Polet Mora', brand: 'Polet', manufacturer: 'Meals de Colombia', share: 0.08, isOwn: true, attributes: { flavor: 'mora', format: 'paleta_agua', positioning: 'mainstream', price: 1200 } },
  { id: 'POLET-LIMON', name: 'Polet Limon', brand: 'Polet', manufacturer: 'Meals de Colombia', share: 0.06, isOwn: true, attributes: { flavor: 'limon', format: 'paleta_agua', positioning: 'mainstream', price: 1200 } },
  { id: 'POLET-COCO', name: 'Polet Coco', brand: 'Polet', manufacturer: 'Meals de Colombia', share: 0.05, isOwn: true, attributes: { flavor: 'coco', format: 'paleta_crema', positioning: 'mainstream', price: 1800 } },
  { id: 'POLET-FRESA', name: 'Polet Fresa', brand: 'Polet', manufacturer: 'Meals de Colombia', share: 0.04, isOwn: true, attributes: { flavor: 'fresa', format: 'paleta_crema', positioning: 'mainstream', price: 1800 } },
  { id: 'CHOCORICO-BAR', name: 'Choco Rico Barra', brand: 'Choco Rico', manufacturer: 'Meals de Colombia', share: 0.07, isOwn: true, attributes: { flavor: 'chocolate', format: 'paleta_crema', positioning: 'kids', price: 2200 } },
  { id: 'CHOCORICO-GALLETA', name: 'Choco Rico Galleta', brand: 'Choco Rico', manufacturer: 'Meals de Colombia', share: 0.05, isOwn: true, attributes: { flavor: 'chocolate_galleta', format: 'paleta_crema', positioning: 'kids', price: 2500 } },
  { id: 'FRESKITO-VAINILLA', name: 'Freskito Vainilla 1L', brand: 'Freskito', manufacturer: 'Meals de Colombia', share: 0.04, isOwn: true, attributes: { flavor: 'vainilla', format: 'litro_familiar', positioning: 'value', price: 9500 } },
  { id: 'FRESKITO-CHOCOLATE', name: 'Freskito Chocolate 1L', brand: 'Freskito', manufacturer: 'Meals de Colombia', share: 0.03, isOwn: true, attributes: { flavor: 'chocolate', format: 'litro_familiar', positioning: 'value', price: 9500 } },
  { id: 'POLET-CONO-VAINILLA', name: 'Polet Cono Vainilla', brand: 'Polet', manufacturer: 'Meals de Colombia', share: 0.03, isOwn: true, attributes: { flavor: 'vainilla', format: 'cono', positioning: 'mainstream', price: 3200 } },
];

const COMPETITOR_PRODUCTS: ExistingProduct[] = [
  { id: 'CREM-HELADO-1', name: 'Crem Helado Vainilla', brand: 'Crem Helado', manufacturer: 'Unilever Colombia', share: 0.15, isOwn: false, attributes: { flavor: 'vainilla', format: 'paleta_crema', positioning: 'mainstream', price: 1800 } },
  { id: 'CREM-HELADO-2', name: 'Crem Helado Chocolate', brand: 'Crem Helado', manufacturer: 'Unilever Colombia', share: 0.12, isOwn: false, attributes: { flavor: 'chocolate', format: 'paleta_crema', positioning: 'mainstream', price: 1800 } },
  { id: 'CREM-HELADO-3', name: 'Crem Helado Cono', brand: 'Crem Helado', manufacturer: 'Unilever Colombia', share: 0.10, isOwn: false, attributes: { flavor: 'vainilla', format: 'cono', positioning: 'mainstream', price: 3500 } },
  { id: 'CREM-HELADO-4', name: 'Crem Helado Kids', brand: 'Crem Helado', manufacturer: 'Unilever Colombia', share: 0.05, isOwn: false, attributes: { flavor: 'chocolate', format: 'paleta_crema', positioning: 'kids', price: 2000 } },
  { id: 'COLOMBINA-1', name: 'Colombina Bon Bon Bum Ice', brand: 'Colombina', manufacturer: 'Colombina', share: 0.05, isOwn: false, attributes: { flavor: 'fresa', format: 'paleta_agua', positioning: 'kids', price: 1500 } },
  { id: 'COLOMBINA-2', name: 'Colombina Cremosa', brand: 'Colombina', manufacturer: 'Colombina', share: 0.03, isOwn: false, attributes: { flavor: 'vainilla', format: 'paleta_crema', positioning: 'mainstream', price: 1600 } },
  { id: 'MIMOS', name: "Mimo's Premium", brand: "Mimo's", manufacturer: 'Grupo Empresarial', share: 0.05, isOwn: false, attributes: { flavor: 'vainilla', format: 'vaso', positioning: 'premium', price: 4500 } },
  { id: 'POPSY', name: 'Popsy Artesanal', brand: 'Popsy', manufacturer: 'Grupo Nutresa', share: 0.03, isOwn: false, attributes: { flavor: 'arequipe', format: 'vaso', positioning: 'premium', price: 5000 } },
];

// Calculate attribute similarity between new product and existing product
function calculateSimilarity(
  newProductLevels: Record<string, string>,
  existingProduct: ExistingProduct,
  levelNames: Record<string, Record<string, string>>
): number {
  let matchCount = 0;
  let totalAttributes = 0;

  // Map level codes to attribute values
  const newProductAttrs: Record<string, string> = {};
  for (const [attrCode, levelCode] of Object.entries(newProductLevels)) {
    const levelName = levelNames[attrCode]?.[levelCode] || levelCode;
    newProductAttrs[attrCode.toLowerCase()] = levelName.toLowerCase();
  }

  // Check format/formato match
  if (existingProduct.attributes.format && newProductAttrs['format']) {
    totalAttributes++;
    const existingFormat = existingProduct.attributes.format.toLowerCase().replace('_', ' ');
    const newFormat = newProductAttrs['format'].toLowerCase();
    if (newFormat.includes(existingFormat) || existingFormat.includes(newFormat.split(' ')[0])) {
      matchCount++;
    }
  }

  // Check flavor/sabor match
  if (existingProduct.attributes.flavor && newProductAttrs['flavor']) {
    totalAttributes++;
    const existingFlavor = existingProduct.attributes.flavor.toLowerCase();
    const newFlavor = newProductAttrs['flavor'].toLowerCase();
    if (existingFlavor === newFlavor || newFlavor.includes(existingFlavor) || existingFlavor.includes(newFlavor)) {
      matchCount++;
    }
  }

  // Check positioning match
  if (existingProduct.attributes.positioning && newProductAttrs['positioning']) {
    totalAttributes++;
    const existingPos = existingProduct.attributes.positioning.toLowerCase();
    const newPos = newProductAttrs['positioning'].toLowerCase();
    if (existingPos === newPos || newPos.includes(existingPos)) {
      matchCount++;
    }
  }

  // Check brand match (if BRAND attribute exists)
  if (newProductAttrs['brand']) {
    totalAttributes++;
    const newBrand = newProductAttrs['brand'].toLowerCase();
    if (existingProduct.brand.toLowerCase() === newBrand || newBrand.includes(existingProduct.brand.toLowerCase())) {
      matchCount += 1.5; // Brand match is weighted higher
    }
  }

  // Check price proximity
  if (existingProduct.attributes.price && newProductAttrs['price']) {
    totalAttributes++;
    const existingPrice = existingProduct.attributes.price;
    const newPriceStr = newProductAttrs['price'].replace(/[^0-9]/g, '');
    const newPrice = parseInt(newPriceStr) || 0;
    if (newPrice > 0) {
      const priceDiff = Math.abs(existingPrice - newPrice) / existingPrice;
      if (priceDiff < 0.15) matchCount += 1; // Within 15%
      else if (priceDiff < 0.30) matchCount += 0.5; // Within 30%
    }
  }

  return totalAttributes > 0 ? matchCount / totalAttributes : 0.2; // Default similarity if no matches
}

// Run market impact simulation
function simulateMarketImpact(
  winningProduct: ProductCombination,
  levelNames: Record<string, Record<string, string>>,
  clientId?: string
): MarketImpactResult {
  const allProducts = [...MEALS_PORTFOLIO, ...COMPETITOR_PRODUCTS];
  const ownProducts = MEALS_PORTFOLIO;
  const competitorProducts = COMPETITOR_PRODUCTS;

  // Calculate total existing market share
  const totalExistingShare = allProducts.reduce((sum, p) => sum + p.share, 0);

  // Estimate new product's potential market share (based on utility and market size)
  // A new product typically captures 1-5% of the market
  const baseNewProductShare = Math.min(0.05, Math.max(0.01, winningProduct.utility * 0.015));

  // Calculate similarity-weighted share loss for each product
  const shareMovements: ShareMovement[] = [];
  let totalSimilarity = 0;

  for (const product of allProducts) {
    const similarity = calculateSimilarity(winningProduct.levels, product, levelNames);
    totalSimilarity += similarity * product.share;
  }

  // Distribute the new product's share gain proportionally based on similarity
  let ownShareLost = 0;
  let competitorShareLost = 0;

  for (const product of allProducts) {
    const similarity = calculateSimilarity(winningProduct.levels, product, levelNames);
    const shareLost = totalSimilarity > 0
      ? (similarity * product.share / totalSimilarity) * baseNewProductShare * 0.85 // 85% comes from existing products
      : 0;

    shareMovements.push({
      product,
      shareLost,
      newShare: product.share - shareLost,
    });

    if (product.isOwn) {
      ownShareLost += shareLost;
    } else {
      competitorShareLost += shareLost;
    }
  }

  // Sort by share lost (descending)
  shareMovements.sort((a, b) => b.shareLost - a.shareLost);

  // Market expansion (new consumers entering category)
  const marketExpansion = baseNewProductShare * 0.15; // 15% comes from market expansion

  // Calculate source of volume
  const sourceOfVolume: SourceOfVolume = {
    competitorConquest: competitorShareLost / baseNewProductShare,
    ownCannibalization: ownShareLost / baseNewProductShare,
    marketExpansion: marketExpansion / baseNewProductShare,
  };

  // Calculate cannibalization rate
  const cannibalizationRate = ownShareLost / baseNewProductShare;
  const cannibalizationRisk: 'low' | 'medium' | 'high' =
    cannibalizationRate < 0.20 ? 'low' :
    cannibalizationRate < 0.40 ? 'medium' : 'high';

  // Generate insights
  const insights: string[] = [];

  // Top competitor being cannibalized
  const topCompetitorLoss = shareMovements.find(m => !m.product.isOwn);
  if (topCompetitorLoss && topCompetitorLoss.shareLost > 0.001) {
    insights.push(`Highest competitor impact: ${topCompetitorLoss.product.name} loses ${(topCompetitorLoss.shareLost * 100).toFixed(2)}% share`);
  }

  // Top own product being cannibalized
  const topOwnLoss = shareMovements.find(m => m.product.isOwn);
  if (topOwnLoss && topOwnLoss.shareLost > 0.001) {
    insights.push(`Highest own-portfolio impact: ${topOwnLoss.product.name} loses ${(topOwnLoss.shareLost * 100).toFixed(2)}% share`);
  }

  // Cannibalization warning
  if (cannibalizationRisk === 'high') {
    insights.push(`⚠️ High cannibalization risk (${(cannibalizationRate * 100).toFixed(0)}%) - consider differentiation strategy`);
  } else if (cannibalizationRisk === 'medium') {
    insights.push(`⚡ Moderate cannibalization (${(cannibalizationRate * 100).toFixed(0)}%) - monitor portfolio overlap`);
  } else {
    insights.push(`✓ Low cannibalization risk - good incrementality profile`);
  }

  // Competitor conquest insight
  if (sourceOfVolume.competitorConquest > 0.5) {
    insights.push(`Strong competitive positioning: ${(sourceOfVolume.competitorConquest * 100).toFixed(0)}% volume from competitors`);
  }

  return {
    newProductShare: baseNewProductShare,
    sourceOfVolume,
    shareMovements,
    cannibalizationRate,
    cannibalizationRisk,
    insights,
  };
}

export function SimulationResults({ result, attributeNames, levelNames, clientId, onClose }: SimulationResultsProps) {
  const [activeTab, setActiveTab] = useState<'concepts' | 'market'>('concepts');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    insights: true,
    topProducts: true,
    importance: true,
    allProducts: false,
    sourceOfVolume: true,
    shareMovements: true,
    marketInsights: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getLevelName = (attrCode: string, levelCode: string): string => {
    return levelNames[attrCode]?.[levelCode] || levelCode;
  };

  const getAttributeName = (attrCode: string): string => {
    return attributeNames[attrCode] || attrCode;
  };

  // Sort importance for display
  const sortedImportance = Object.entries(result.attribute_importance)
    .sort((a, b) => b[1] - a[1]);

  // Calculate market impact for winning product
  const marketImpact = useMemo(() => {
    if (result.top_products.length === 0) return null;
    return simulateMarketImpact(result.top_products[0], levelNames, clientId);
  }, [result.top_products, levelNames, clientId]);

  // Get winning product description
  const winningProductDesc = useMemo(() => {
    if (result.top_products.length === 0) return '';
    return Object.entries(result.top_products[0].levels)
      .map(([attr, level]) => getLevelName(attr, level))
      .join(' + ');
  }, [result.top_products, levelNames]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">{result.name}</h2>
            <p className="text-sm text-text-secondary">
              {result.total_combinations.toLocaleString()} product combinations analyzed
            </p>
          </div>
          {onClose && (
            <button onClick={onClose} className="btn btn-secondary text-sm">
              New Simulation
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-surface-600 pb-0">
        <button
          onClick={() => setActiveTab('concepts')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'concepts'
              ? 'text-primary-400 border-primary-400'
              : 'text-text-secondary border-transparent hover:text-text-secondary'
          }`}
        >
          Concept Results
        </button>
        <button
          onClick={() => setActiveTab('market')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'market'
              ? 'text-primary-400 border-primary-400'
              : 'text-text-secondary border-transparent hover:text-text-secondary'
          }`}
        >
          Market Impact
          {marketImpact && marketImpact.cannibalizationRisk === 'high' && (
            <span className="ml-2 px-1.5 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">!</span>
          )}
        </button>
      </div>

      {/* CONCEPT RESULTS TAB */}
      {activeTab === 'concepts' && (
        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-primary-400">
                {result.total_combinations.toLocaleString()}
              </div>
              <div className="text-xs text-text-secondary mt-1">Total Combinations</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-primary-400">{result.attributes_used}</div>
              <div className="text-xs text-text-secondary mt-1">Attributes</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-emerald-400">{result.levels_used}</div>
              <div className="text-xs text-text-secondary mt-1">Levels</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-amber-400">
                {(result.top_products[0]?.share * 100).toFixed(1)}%
              </div>
              <div className="text-xs text-text-secondary mt-1">Top Concept Share</div>
            </div>
          </div>

          {/* Insights */}
          <div className="card overflow-hidden">
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-surface-700/50"
              onClick={() => toggleSection('insights')}
            >
              <div className="flex items-center gap-2">
                {expandedSections.insights ? (
                  <ChevronDownIcon className="w-4 h-4 text-text-secondary" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 text-text-secondary" />
                )}
                <h3 className="text-lg font-semibold text-white">Key Insights</h3>
                <span className="px-2 py-0.5 bg-primary-500/20 text-primary-400 text-xs rounded-full">
                  {result.insights.length}
                </span>
              </div>
            </div>
            {expandedSections.insights && (
              <div className="px-4 pb-4 space-y-2">
                {result.insights.map((insight, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-surface-700/50 rounded-lg">
                    <span className="text-primary-400 text-lg">💡</span>
                    <p className="text-text-secondary">{insight}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Attribute Importance */}
          <div className="card overflow-hidden">
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-surface-700/50"
              onClick={() => toggleSection('importance')}
            >
              <div className="flex items-center gap-2">
                {expandedSections.importance ? (
                  <ChevronDownIcon className="w-4 h-4 text-text-secondary" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 text-text-secondary" />
                )}
                <h3 className="text-lg font-semibold text-white">Attribute Importance</h3>
              </div>
            </div>
            {expandedSections.importance && (
              <div className="px-4 pb-4 space-y-3">
                {sortedImportance.map(([attrCode, importance]) => (
                  <div key={attrCode} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-secondary">{getAttributeName(attrCode)}</span>
                      <span className="text-sm font-medium text-primary-400">
                        {(importance * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="h-2 bg-surface-600 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-primary-500 to-primary-400 rounded-full transition-all"
                        style={{ width: `${importance * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top Products */}
          <div className="card overflow-hidden">
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-surface-700/50"
              onClick={() => toggleSection('topProducts')}
            >
              <div className="flex items-center gap-2">
                {expandedSections.topProducts ? (
                  <ChevronDownIcon className="w-4 h-4 text-text-secondary" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 text-text-secondary" />
                )}
                <h3 className="text-lg font-semibold text-white">Top 10 Product Combinations</h3>
              </div>
            </div>
            {expandedSections.topProducts && (
              <div className="px-4 pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-text-secondary border-b border-surface-600">
                        <th className="pb-2 pr-4">Rank</th>
                        {Object.keys(result.top_products[0]?.levels || {}).map(attrCode => (
                          <th key={attrCode} className="pb-2 pr-4">{getAttributeName(attrCode)}</th>
                        ))}
                        <th className="pb-2 pr-4 text-right">Share</th>
                        <th className="pb-2 text-right">Utility</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.top_products.map((product) => (
                        <tr
                          key={product.id}
                          className="border-b border-surface-700/50 hover:bg-surface-700/30"
                        >
                          <td className="py-3 pr-4">
                            <span className={`
                              inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold
                              ${product.rank === 1 ? 'bg-amber-500/20 text-amber-400' :
                                product.rank === 2 ? 'bg-gray-400/20 text-text-secondary' :
                                product.rank === 3 ? 'bg-orange-500/20 text-orange-400' :
                                'bg-surface-600 text-text-secondary'}
                            `}>
                              {product.rank}
                            </span>
                          </td>
                          {Object.entries(product.levels).map(([attrCode, levelCode]) => (
                            <td key={attrCode} className="py-3 pr-4 text-sm text-text-secondary">
                              {getLevelName(attrCode, levelCode)}
                            </td>
                          ))}
                          <td className="py-3 pr-4 text-right">
                            <span className="text-primary-400 font-medium">
                              {(product.share * 100).toFixed(1)}%
                            </span>
                          </td>
                          <td className="py-3 text-right text-sm text-text-secondary">
                            {product.utility.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* All Products (Expandable) */}
          <div className="card overflow-hidden">
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-surface-700/50"
              onClick={() => toggleSection('allProducts')}
            >
              <div className="flex items-center gap-2">
                {expandedSections.allProducts ? (
                  <ChevronDownIcon className="w-4 h-4 text-text-secondary" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 text-text-secondary" />
                )}
                <h3 className="text-lg font-semibold text-white">All Products</h3>
                <span className="text-sm text-text-tertiary">
                  ({result.all_products.length.toLocaleString()} combinations)
                </span>
              </div>
            </div>
            {expandedSections.allProducts && (
              <div className="px-4 pb-4">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-surface-800">
                      <tr className="text-left text-text-secondary border-b border-surface-600">
                        <th className="pb-2 pr-4">Rank</th>
                        {Object.keys(result.all_products[0]?.levels || {}).map(attrCode => (
                          <th key={attrCode} className="pb-2 pr-4">{getAttributeName(attrCode)}</th>
                        ))}
                        <th className="pb-2 text-right">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.all_products.map((product) => (
                        <tr
                          key={product.id}
                          className="border-b border-surface-700/30 hover:bg-surface-700/20"
                        >
                          <td className="py-2 pr-4 text-text-tertiary">{product.rank}</td>
                          {Object.entries(product.levels).map(([attrCode, levelCode]) => (
                            <td key={attrCode} className="py-2 pr-4 text-text-secondary">
                              {getLevelName(attrCode, levelCode)}
                            </td>
                          ))}
                          <td className="py-2 text-right text-text-secondary">
                            {(product.share * 100).toFixed(2)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MARKET IMPACT TAB */}
      {activeTab === 'market' && marketImpact && (
        <div className="space-y-4">
          {/* Winning Product Banner */}
          <div className="card p-4 bg-surface-700/50 border-l-4 border-primary-500">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Analyzing Market Impact For</div>
                <div className="text-lg font-semibold text-white">{winningProductDesc}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">Expected Market Share</div>
                <div className="text-2xl font-bold text-primary-400">{(marketImpact.newProductShare * 100).toFixed(1)}%</div>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-4 gap-4">
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-emerald-400">
                {(marketImpact.sourceOfVolume.competitorConquest * 100).toFixed(0)}%
              </div>
              <div className="text-xs text-text-secondary mt-1">From Competitors</div>
            </div>
            <div className="card p-4 text-center">
              <div className={`text-2xl font-bold ${
                marketImpact.cannibalizationRisk === 'high' ? 'text-red-400' :
                marketImpact.cannibalizationRisk === 'medium' ? 'text-amber-400' :
                'text-emerald-400'
              }`}>
                {(marketImpact.sourceOfVolume.ownCannibalization * 100).toFixed(0)}%
              </div>
              <div className="text-xs text-text-secondary mt-1">Own Cannibalization</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-primary-400">
                {(marketImpact.sourceOfVolume.marketExpansion * 100).toFixed(0)}%
              </div>
              <div className="text-xs text-text-secondary mt-1">Market Expansion</div>
            </div>
            <div className="card p-4 text-center">
              <div className={`text-2xl font-bold ${
                marketImpact.cannibalizationRisk === 'high' ? 'text-red-400' :
                marketImpact.cannibalizationRisk === 'medium' ? 'text-amber-400' :
                'text-emerald-400'
              }`}>
                {marketImpact.cannibalizationRisk.toUpperCase()}
              </div>
              <div className="text-xs text-text-secondary mt-1">Cannibalization Risk</div>
            </div>
          </div>

          {/* Source of Volume Chart */}
          <div className="card overflow-hidden">
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-surface-700/50"
              onClick={() => toggleSection('sourceOfVolume')}
            >
              <div className="flex items-center gap-2">
                {expandedSections.sourceOfVolume ? (
                  <ChevronDownIcon className="w-4 h-4 text-text-secondary" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 text-text-secondary" />
                )}
                <h3 className="text-lg font-semibold text-white">Source of Volume</h3>
              </div>
            </div>
            {expandedSections.sourceOfVolume && (
              <div className="px-4 pb-4">
                <div className="flex items-center gap-6">
                  {/* Pie Chart Visual */}
                  <div className="relative w-40 h-40">
                    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                      {/* Competitor Conquest (green) */}
                      <circle
                        cx="50" cy="50" r="40"
                        fill="transparent"
                        stroke="#10b981"
                        strokeWidth="20"
                        strokeDasharray={`${marketImpact.sourceOfVolume.competitorConquest * 251.2} 251.2`}
                      />
                      {/* Own Cannibalization (red/amber) */}
                      <circle
                        cx="50" cy="50" r="40"
                        fill="transparent"
                        stroke={marketImpact.cannibalizationRisk === 'high' ? '#ef4444' : '#f59e0b'}
                        strokeWidth="20"
                        strokeDasharray={`${marketImpact.sourceOfVolume.ownCannibalization * 251.2} 251.2`}
                        strokeDashoffset={`${-marketImpact.sourceOfVolume.competitorConquest * 251.2}`}
                      />
                      {/* Market Expansion (cyan) */}
                      <circle
                        cx="50" cy="50" r="40"
                        fill="transparent"
                        stroke="#06b6d4"
                        strokeWidth="20"
                        strokeDasharray={`${marketImpact.sourceOfVolume.marketExpansion * 251.2} 251.2`}
                        strokeDashoffset={`${-(marketImpact.sourceOfVolume.competitorConquest + marketImpact.sourceOfVolume.ownCannibalization) * 251.2}`}
                      />
                    </svg>
                  </div>
                  {/* Legend */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        <span className="text-sm text-text-secondary">Competitor Conquest</span>
                      </div>
                      <span className="text-sm font-medium text-emerald-400">
                        {(marketImpact.sourceOfVolume.competitorConquest * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${
                          marketImpact.cannibalizationRisk === 'high' ? 'bg-red-500' : 'bg-amber-500'
                        }`} />
                        <span className="text-sm text-text-secondary">Own Cannibalization</span>
                      </div>
                      <span className={`text-sm font-medium ${
                        marketImpact.cannibalizationRisk === 'high' ? 'text-red-400' : 'text-amber-400'
                      }`}>
                        {(marketImpact.sourceOfVolume.ownCannibalization * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-primary-500" />
                        <span className="text-sm text-text-secondary">Market Expansion</span>
                      </div>
                      <span className="text-sm font-medium text-primary-400">
                        {(marketImpact.sourceOfVolume.marketExpansion * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Share Movement Table */}
          <div className="card overflow-hidden">
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-surface-700/50"
              onClick={() => toggleSection('shareMovements')}
            >
              <div className="flex items-center gap-2">
                {expandedSections.shareMovements ? (
                  <ChevronDownIcon className="w-4 h-4 text-text-secondary" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 text-text-secondary" />
                )}
                <h3 className="text-lg font-semibold text-white">Share Movement by Product</h3>
              </div>
            </div>
            {expandedSections.shareMovements && (
              <div className="px-4 pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-text-secondary border-b border-surface-600">
                        <th className="pb-2 pr-4">Product</th>
                        <th className="pb-2 pr-4">Brand</th>
                        <th className="pb-2 pr-4">Owner</th>
                        <th className="pb-2 pr-4 text-right">Current Share</th>
                        <th className="pb-2 pr-4 text-right">Share Lost</th>
                        <th className="pb-2 text-right">New Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marketImpact.shareMovements
                        .filter(m => m.shareLost > 0.0001)
                        .slice(0, 15)
                        .map((movement) => (
                        <tr
                          key={movement.product.id}
                          className={`border-b border-surface-700/50 hover:bg-surface-700/30 ${
                            movement.product.isOwn ? 'bg-amber-500/5' : ''
                          }`}
                        >
                          <td className="py-3 pr-4 text-sm text-text-secondary">
                            {movement.product.name}
                          </td>
                          <td className="py-3 pr-4 text-sm text-text-secondary">
                            {movement.product.brand}
                          </td>
                          <td className="py-3 pr-4">
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              movement.product.isOwn
                                ? 'bg-amber-500/20 text-amber-400'
                                : 'bg-gray-500/20 text-text-secondary'
                            }`}>
                              {movement.product.isOwn ? 'Own' : 'Competitor'}
                            </span>
                          </td>
                          <td className="py-3 pr-4 text-right text-sm text-text-secondary">
                            {(movement.product.share * 100).toFixed(2)}%
                          </td>
                          <td className="py-3 pr-4 text-right">
                            <span className={`text-sm font-medium ${
                              movement.product.isOwn ? 'text-red-400' : 'text-emerald-400'
                            }`}>
                              -{(movement.shareLost * 100).toFixed(2)}%
                            </span>
                          </td>
                          <td className="py-3 text-right text-sm text-text-secondary">
                            {(movement.newShare * 100).toFixed(2)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Market Insights */}
          <div className="card overflow-hidden">
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-surface-700/50"
              onClick={() => toggleSection('marketInsights')}
            >
              <div className="flex items-center gap-2">
                {expandedSections.marketInsights ? (
                  <ChevronDownIcon className="w-4 h-4 text-text-secondary" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 text-text-secondary" />
                )}
                <h3 className="text-lg font-semibold text-white">Market Impact Insights</h3>
              </div>
            </div>
            {expandedSections.marketInsights && (
              <div className="px-4 pb-4 space-y-2">
                {marketImpact.insights.map((insight, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-surface-700/50 rounded-lg">
                    <span className="text-lg">
                      {insight.startsWith('⚠️') ? '' : insight.startsWith('⚡') ? '' : insight.startsWith('✓') ? '' : '📊'}
                    </span>
                    <p className="text-text-secondary">{insight}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SimulationResults;
